import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QrCode, User, Eye, EyeOff } from "lucide-react";
import Navbar from "@/components/modules/Navbar";
import useAxios from "@/hooks/use-axios";




export default function UserLogin() {
  const api = useAxios();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    company: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isRegister) {
        await api.post(`/user/register`, {
          email: formData.email,
          password: formData.password,
          name: formData.name,
          company: formData.company || null,
        });
        toast.success("Registration successful! Please login.");
        setIsRegister(false);
        setFormData({ ...formData, password: "" });
      } else {
        const response = await api.post(`/user/login`, {
          email: formData.email,
          password: formData.password,
        });
        localStorage.setItem("user_token", response.data.access_token);
        toast.success("Login successful");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col">
      
      <Navbar hideNavigation />

      <div className="flex-1 flex items-center justify-center p-4 my-auto">
        <div className="w-full max-w-md">
          <div className="rounded-lg p-8 bg-dark-foreground">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-primary-500/20 flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-primary-500" />
              </div>
              <h1 className="text-2xl font-bold text-white">
                {isRegister ? "Create Account" : "User Login"}
              </h1>
              <p className="text-sm text-zinc-400 mt-2">
                {isRegister
                  ? "Register to purchase packages and get API tokens"
                  : "Login to manage your tokens and subscriptions"}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegister && (
                <>
                  <div>
                    <Label className="text-zinc-300">Full Name</Label>
                    <Input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 bg-zinc-950 border-zinc-800"
                      placeholder="John Doe"
                      required
                      data-testid="name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Company (Optional)</Label>
                    <Input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="mt-1 bg-zinc-950 border-zinc-800"
                      placeholder="Your Company"
                      data-testid="company-input"
                    />
                  </div>
                </>
              )}

              <div>
                <Label className="text-zinc-300">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 bg-zinc-950 border-zinc-800"
                  placeholder="you@example.com"
                  required
                  data-testid="email-input"
                />
              </div>

              <div>
                <Label className="text-zinc-300">Password</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 pr-10"
                    placeholder="••••••••"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full"
                size="xl"
                data-testid="submit-btn"
              >
                {isLoading ? <div className="spinner" /> : isRegister ? "Register" : "Login"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsRegister(!isRegister)}
                className="text-sm text-zinc-400 hover:text-primary-400 transition-colors"
                data-testid="toggle-mode"
              >
                {isRegister ? "Already have an account? Login" : "Need an account? Register"}
              </button>
            </div>
          </div>

          <p className="text-center text-xs text-zinc-500 mt-6">
            <Link to="/" className="hover:text-primary-400 transition-colors">
              ← Back to Demo
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
